import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ticketsService from "../services/ticketsService";
import s3Service from "../services/s3Service";
import { toast } from "react-toastify";
import "../assets/panels.css";
import Loader from "../components/Loader";
import { saveDraft, isQuotaError } from "../lib/offlineDb";
import { triggerSync } from "../lib/inspectionSyncEngine";
import { certificateTypeFromServiceName } from "../utils/certificateTypes";

const genUuid = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : "xxxxxxxxxxxx4xxxyxxxxxxxxxxxxxxx".replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
      });

const initialFormState = {
  property_id: "",
  room_id: "", // Laravel-compatible: accepted in request but not stored in database
  type_id: "",
  urgency_id: "",
  status_id: "",
  description: "",
  internal_note: "",
  date_estimated_completion: "",
  date_completed: "",
  book_in_date: "",
  book_in_time_from: "",
  book_in_time_to: "",
  cost_of_works: "",
  assigned_member_id: "",
  contractor_id: "",
  contractor_contact_id: "",
  equipment_purchased: "",
  client_view: "0",
  guardian_view: "1",
  is_certificate_renewal: "0", // Staff-only: enables the certificate upload/review flow
  is_complaint: "0",
  complaint_id: "",
  issue_with: "",
};

const TicketEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState(null);
  const [notes, setNotes] = useState([]);
  const [form, setForm] = useState(initialFormState);
  const [dropdowns, setDropdowns] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});


  const [deleteLoadingId, setDeleteLoadingId] = useState(null);
  const [deleteError, setDeleteError] = useState(null);
  const [propertySearch, setPropertySearch] = useState("");
  const [showPropertyDropdown, setShowPropertyDropdown] = useState(false);
  const [searchingProperties, setSearchingProperties] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [searchRooms, setSearchRooms] = useState([]);
  const propertyDropdownRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  // Form state persistence key
  const formStateKey = `ticket_edit_form_${id}`;

  // Save form state to localStorage
  const saveFormState = (formData) => {
    try {
      localStorage.setItem(formStateKey, JSON.stringify(formData));
    } catch (error) {
      console.warn("Failed to save form state to localStorage:", error);
    }
  };

  // Load form state from localStorage
  const loadFormState = () => {
    try {
      const savedState = localStorage.getItem(formStateKey);
      if (savedState) {
        return JSON.parse(savedState);
      }
    } catch (error) {
      console.warn("Failed to load form state from localStorage:", error);
    }
    return null;
  };

  // Clear saved form state
  const clearFormState = () => {
    try {
      localStorage.removeItem(formStateKey);
    } catch (error) {
      console.warn("Failed to clear form state from localStorage:", error);
    }
  };

  // Use API search results for property dropdown
  const filteredProperties = searchResults;

  // Debounced property search via API
  const searchPropertiesApi = (query) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (!query || query.trim().length < 2) {
      setSearchResults([]);
      setSearchingProperties(false);
      return;
    }

    setSearchingProperties(true);
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(
          `${process.env.REACT_APP_API_URL || ""}/api/tickets/search-properties?q=${encodeURIComponent(query.trim())}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (response.ok) {
          const data = await response.json();
          setSearchResults(data.properties || []);
          setSearchRooms(prev => {
            // Merge new rooms with existing (keep rooms for currently selected property)
            const newIds = new Set((data.rooms || []).map(r => r.id));
            const kept = prev.filter(r => !newIds.has(r.id) && String(r.property_id) === String(form.property_id));
            return [...kept, ...(data.rooms || [])];
          });
        }
      } catch (err) {
        console.error("Property search error:", err);
      } finally {
        setSearchingProperties(false);
      }
    }, 300);
  };

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Close property dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (propertyDropdownRef.current && !propertyDropdownRef.current.contains(event.target)) {
        setShowPropertyDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const loadAllData = async () => {
      setLoading(true);
      setError(null);

      try {
        // Fetch ticket data and notes first (small payloads, unlikely to fail)
        const [ticketData, notesData] = await Promise.all([
          ticketsService.getTicket(id),
          ticketsService.getTicketNotes(id),
        ]);

        console.log("Ticket data received:", ticketData);
        setTicket(ticketData);
        setNotes(notesData);

        // Fetch dropdown data separately with built-in retry (large payload, can fail with HTTP2 errors)
        const dropdownData = await ticketsService.getEditData();
        console.log("Dropdown data received:", dropdownData);
        setDropdowns(dropdownData);

        // Properly map ticket data to form fields
        const mappedFormData = {
          ...initialFormState,
          // Basic fields
          property_id: ticketData.property_id || "",
          // Extract room_id from polymorphic relationship
          // CRITICAL: Check ticketable_type FIRST, then fall back to ticketData.room_id
          // This ensures we always get the room_id even if backend didn't set it
          room_id: (() => {
            // Check if ticketable_type indicates a room
            // Handle various formats: JSON-encoded backslashes, escaped backslashes, forward slashes, short format
            const isRoomType = ticketData.ticketable_type === 'App\\Rooms\\Room' ||
                               ticketData.ticketable_type === 'App\\\\Rooms\\\\Room' ||
                               ticketData.ticketable_type === 'App/Rooms/Room' ||
                               ticketData.ticketable_type === 'rooms';

            if (isRoomType && ticketData.ticketable_id) {
              console.log("✅ Using ticketable_id as room_id:", {
                ticketable_type: ticketData.ticketable_type,
                ticketable_id: ticketData.ticketable_id,
                ticket_room_id: ticketData.room_id
              });
              return String(ticketData.ticketable_id);
            }

            // Fall back to ticketData.room_id if set by backend
            if (ticketData.room_id) {
              console.log("✅ Using ticketData.room_id:", ticketData.room_id);
              return String(ticketData.room_id);
            }

            console.log("⚠️ No room_id found in ticket data");
            return "";
          })(),

          type_id: ticketData.type?.id || ticketData.type_id || "",
          urgency_id: ticketData.urgency?.id || ticketData.urgency_id || "",
          status_id: ticketData.status?.id || ticketData.status_id || "",
          description: ticketData.description || "",
          internal_note: ticketData.internal_note || "",
          date_estimated_completion: ticketData.date_estimated_completion
            ? typeof ticketData.date_estimated_completion === "string"
              ? ticketData.date_estimated_completion.split("T")[0]
              : new Date(ticketData.date_estimated_completion)
                  .toISOString()
                  .split("T")[0]
            : "",
          date_completed: ticketData.date_completed
            ? (() => {
                const val = ticketData.date_completed;
                // Handle ISO format (2025-11-16T00:00:00.000Z)
                if (typeof val === "string" && val.includes("T")) {
                  return val.split("T")[0];
                }
                // Handle DD/MM/YYYY format (05/11/2025) - backend uses this format
                if (typeof val === "string" && val.includes("/")) {
                  const parts = val.split("/");
                  if (parts.length === 3) {
                    // DD/MM/YYYY format -> YYYY-MM-DD
                    return `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
                  }
                }
                // Handle YYYY-MM-DD format (already correct)
                if (typeof val === "string" && /^\d{4}-\d{2}-\d{2}$/.test(val)) {
                  return val;
                }
                // Fallback: try to parse as date
                const date = new Date(val);
                return isNaN(date.getTime()) ? "" : date.toISOString().split("T")[0];
              })()
            : "",
          book_in_date: ticketData.book_in_date
            ? typeof ticketData.book_in_date === "string"
              ? ticketData.book_in_date.split("T")[0]
              : new Date(ticketData.book_in_date).toISOString().split("T")[0]
            : "",
          book_in_time_from: ticketData.book_in_time_from || "",
          book_in_time_to: ticketData.book_in_time_to || "",
          cost_of_works: ticketData.cost_of_works || "",
          assigned_member_id:
            ticketData.assignedMember?.id ||
            ticketData.assigned_member_id ||
            "",
          contractor_id:
            ticketData.contractor?.id || ticketData.contractor_id || "",
          contractor_contact_id: ticketData.contractor_contact_id || "",
          equipment_purchased: ticketData.equipment_purchased || "",
          client_view: ticketData.client_view ? "1" : "0",
          guardian_view: ticketData.guardian_view ? "1" : "0",
          is_certificate_renewal: ticketData.is_certificate_renewal ? "1" : "0",
          is_complaint: ticketData.is_complaint ? "1" : "0",
          complaint_id:
            ticketData.is_complaint && ticketData.status_id
              ? String(ticketData.status_id)
              : "",
          issue_with: ticketData.issue_with || "",
        };

        // Try to load saved form state, otherwise use mapped data
        const savedFormState = loadFormState();
        // CRITICAL: Always prioritize ticket's room_id over saved state
        // The ticket's room_id comes from the database and should always be used on initial load
        const finalFormData = savedFormState
          ? {
              ...savedFormState,
              // Always use ticket's room_id if it exists, otherwise use saved state
              room_id: mappedFormData.room_id ? String(mappedFormData.room_id) : (savedFormState.room_id ? String(savedFormState.room_id) : "")
            }
          : mappedFormData;

        console.log("Mapped form data:", mappedFormData);
        console.log("Saved form state:", savedFormState);
        console.log("Final form data:", finalFormData);

        // Load the ticket's current property and its rooms via exact ID lookup
        if (finalFormData.property_id) {
          try {
            const token = localStorage.getItem("token");
            // Use id= parameter for exact database ID lookup (not q= which requires 2+ chars)
            const propRes = await fetch(
              `${process.env.REACT_APP_API_URL || ""}/api/tickets/search-properties?id=${encodeURIComponent(finalFormData.property_id)}`,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            if (propRes.ok) {
              const propData = await propRes.json();
              if (propData.properties?.length > 0) {
                setSearchResults(propData.properties);
                setSearchRooms(propData.rooms || []);
                const currentProperty = propData.properties[0];
                setPropertySearch(`${currentProperty.property_id || currentProperty.id} - ${currentProperty.name}`);
              }
            }
          } catch (propErr) {
            console.error("Error loading ticket property:", propErr);
          }
        }

        console.log("🔍 Room ID Debug:", {
          ticket_room_id: ticketData.room_id,
          ticket_room_id_type: typeof ticketData.room_id,
          ticketable_type: ticketData.ticketable_type,
          ticketable_id: ticketData.ticketable_id,
          ticketable_id_type: typeof ticketData.ticketable_id,
          mapped_room_id: mappedFormData.room_id,
          mapped_room_id_type: typeof mappedFormData.room_id,
          saved_room_id: savedFormState?.room_id,
          saved_room_id_type: typeof savedFormState?.room_id,
          final_room_id: finalFormData.room_id,
          final_room_id_type: typeof finalFormData.room_id,
          propertyRoomsCount: propertyRooms.length,
          propertyRoomsIds: propertyRooms.map(r => ({ id: r.id, name: r.name }))
        });
        console.log("Complaint status mapping:", {
          original_complaint_id: ticketData.complaint_id,
          original_complaint_id_type: typeof ticketData.complaint_id,
          original_status_id: ticketData.status_id,
          original_status_id_type: typeof ticketData.status_id,
          is_complaint: ticketData.is_complaint,
          mapped_complaint_id: mappedFormData.complaint_id,
          mapped_complaint_id_type: typeof mappedFormData.complaint_id,
          mapped_is_complaint: mappedFormData.is_complaint,
        });
        setForm(finalFormData);
      } catch (err) {
        console.error("Error loading ticket data:", err);
        setError(err.message || "Failed to load ticket");
      } finally {
        setLoading(false);
      }
    };

    loadAllData();
  }, [id]);

  // Cleanup effect to clear form state when component unmounts
  useEffect(() => {
    return () => {
      // Don't clear form state on unmount - let it persist for navigation back from note edit
    };
  }, []);

  // Update contractor contacts when contractor changes
  const contractorContacts = dropdowns.contractorContacts
    ? dropdowns.contractorContacts.filter(
        (c) => String(c.contractor_id) === String(form.contractor_id)
      )
    : [];

  // CRITICAL: Extract ticket's room_id FIRST so it's available for all logic
  const ticketRoomId = form.room_id ? String(form.room_id).trim() : "";
  
  // Update rooms when property changes - use searchRooms (from API) instead of dropdowns.rooms
  const allRooms = searchRooms.length > 0 ? searchRooms : (dropdowns.rooms || []);
  const rooms = allRooms.filter(
    (r) => String(r.property_id) === String(form.property_id)
  );

  // CRITICAL FIX: If ticket has a room_id that's not in the filtered rooms,
  // include it from all rooms so it shows as selected
  // This handles cases where the room was moved to a different property or the property changed
  if (ticketRoomId && allRooms.length > 0) {
    // Search in ALL rooms (not just filtered by property)
    const ticketRoom = allRooms.find(r => String(r.id).trim() === ticketRoomId);
    
    console.log("🔍 Looking for ticket's room:", {
      ticketRoomId: ticketRoomId,
      totalRoomsInDropdown: dropdowns.rooms.length,
      roomFound: !!ticketRoom,
      roomDetails: ticketRoom ? {
        id: ticketRoom.id,
        name: ticketRoom.name,
        property_id: ticketRoom.property_id
      } : null,
      alreadyInFilteredList: !!rooms.find(r => String(r.id).trim() === ticketRoomId)
    });
    
    if (ticketRoom && !rooms.find(r => String(r.id).trim() === ticketRoomId)) {
      console.log("✅ Adding ticket's room to list:", {
        roomId: ticketRoomId,
        roomName: ticketRoom.name,
        roomPropertyId: ticketRoom.property_id,
        formPropertyId: form.property_id,
        note: ticketRoom.property_id !== form.property_id ? "Room belongs to different property!" : "Room matches property"
      });
      rooms.push(ticketRoom); // Add ticket's room to the list
    } else if (!ticketRoom) {
      console.warn("⚠️ Ticket's room (ID: " + ticketRoomId + ") not found in dropdowns.rooms at all!");
      console.warn("Available room IDs in dropdown:", dropdowns.rooms.map(r => r.id).slice(0, 20));
      
      // FALLBACK: Create a placeholder room object so the selection logic still works
      // This handles cases where the room was deleted or not included in the dropdown
      const placeholderRoom = {
        id: parseInt(ticketRoomId),
        name: `Room ${ticketRoomId} (Not Available)`,
        property_id: form.property_id || null,
        key_number: 0,
        price: 0,
        status_id: null,
        type_id: null
      };
      
      console.log("🔧 Creating placeholder room object:", placeholderRoom);
      if (!rooms.find(r => String(r.id).trim() === ticketRoomId)) {
        rooms.push(placeholderRoom);
      }
    }
  }

  // Remove duplicate room names - keep only the room with the highest ID for each name
  // This handles database data quality issues where multiple rooms have the same name
  // CRITICAL: If ticket's room has a duplicate name, keep ONLY the ticket's room (remove the other)
  const uniqueRoomsByName = rooms.reduce((acc, room) => {
    const roomIdString = String(room.id).trim();
    const isTicketRoom = ticketRoomId && roomIdString === ticketRoomId;
    
    // First, check if this room ID already exists (shouldn't happen, but handle it)
    const existingRoomById = acc.find(r => String(r.id).trim() === roomIdString);
    if (existingRoomById) {
      // Room with this ID already exists, skip it
      return acc;
    }
    
    // Always keep the ticket's room, and remove any other room with the same name
    if (isTicketRoom) {
      // Remove any existing room with the same name (but different ID)
      const existingRoomWithSameName = acc.find(r => r.name === room.name && String(r.id).trim() !== roomIdString);
      if (existingRoomWithSameName) {
        const existingIndex = acc.indexOf(existingRoomWithSameName);
        acc.splice(existingIndex, 1); // Remove the duplicate
        console.log(`🔧 Removed duplicate room "${room.name}" (ID: ${existingRoomWithSameName.id}) to keep ticket's room (ID: ${room.id})`);
      }
      acc.push(room);
      return acc;
    }
    
    // For non-ticket rooms, check if ticket's room already has this name
    // If so, skip this room (ticket's room takes priority)
    if (ticketRoomId) {
      const ticketRoom = rooms.find(r => String(r.id).trim() === ticketRoomId);
      if (ticketRoom && ticketRoom.name === room.name) {
        // Ticket's room already has this name, skip this duplicate
        console.log(`🔧 Skipping duplicate room "${room.name}" (ID: ${room.id}) - ticket's room (ID: ${ticketRoomId}) takes priority`);
        return acc;
      }
    }
    
    // Normal deduplication: check if a room with this name already exists
    const existingRoom = acc.find(r => r.name === room.name);
    if (!existingRoom) {
      acc.push(room);
    } else {
      // Replace with room that has higher ID (more recent)
      const existingIndex = acc.indexOf(existingRoom);
      if (room.id > existingRoom.id) {
        acc[existingIndex] = room;
      }
    }
    return acc;
  }, []);
  
  // FINAL SAFEGUARD: Ensure ticket's room is always in the list
  if (ticketRoomId && !uniqueRoomsByName.find(r => String(r.id).trim() === ticketRoomId)) {
    console.warn("⚠️ CRITICAL: Ticket's room was removed by deduplication! Re-adding it...");
    // Find the original room from the rooms array
    const ticketRoom = rooms.find(r => String(r.id).trim() === ticketRoomId);
    if (ticketRoom) {
      uniqueRoomsByName.push(ticketRoom);
      console.log("✅ Re-added ticket's room to list");
    } else {
      // Create placeholder if original room not found
      const placeholderRoom = {
        id: parseInt(ticketRoomId),
        name: `Room ${ticketRoomId} (Not Available)`,
        property_id: form.property_id || null,
        key_number: 0,
        price: 0,
        status_id: null,
        type_id: null
      };
      uniqueRoomsByName.push(placeholderRoom);
      console.log("✅ Created and added placeholder room");
    }
  }

  // Sort rooms by name for consistent display
  const sortedUniqueRooms = uniqueRoomsByName.sort((a, b) => {
    // Natural sort for room names (e.g., "Room 1", "Room 10", "Room 12A")
    return a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' });
  });

  // Get selected property and its rooms (check searchResults first, then dropdowns)
  const selectedProperty = searchResults.find(
    (p) => String(p.id) === String(form.property_id)
  ) || dropdowns.properties?.find(
    (p) => String(p.id) === String(form.property_id)
  );
  const propertyRooms = sortedUniqueRooms;

  // CRITICAL DEBUG: Log room list and form.room_id to verify they match
  console.log("🔍 FINAL ROOM LIST DEBUG:", {
    form_room_id: form.room_id,
    form_room_id_type: typeof form.room_id,
    form_room_id_string: String(form.room_id || "").trim(),
    propertyRooms_count: propertyRooms.length,
    propertyRooms_ids: propertyRooms.map(r => ({
      id: r.id,
      id_type: typeof r.id,
      id_string: String(r.id).trim(),
      name: r.name,
      matches_form_room_id: String(r.id).trim() === String(form.room_id || "").trim()
    })),
    ticketRoomId: ticketRoomId,
    ticketRoomId_in_list: propertyRooms.some(r => String(r.id).trim() === ticketRoomId)
  });

  // Fix property data structure - ensure postcode is separate from address
  const fixedProperty = selectedProperty
    ? {
        ...selectedProperty,
        // If postcode is empty but one of the address fields contains a postcode pattern, extract it
        postcode:
          selectedProperty.postcode ||
          (selectedProperty.address_3 &&
          /^[A-Z]{1,2}\d[A-Z\d]?\s?\d[A-Z]{2}$/i.test(
            selectedProperty.address_3.trim()
          )
            ? selectedProperty.address_3.trim()
            : null) ||
          (selectedProperty.address_2 &&
          /^[A-Z]{1,2}\d[A-Z\d]?\s?\d[A-Z]{2}$/i.test(
            selectedProperty.address_2.trim()
          )
            ? selectedProperty.address_2.trim()
            : null) ||
          (selectedProperty.address_1 &&
          /^[A-Z]{1,2}\d[A-Z\d]?\s?\d[A-Z]{2}$/i.test(
            selectedProperty.address_1.trim()
          )
            ? selectedProperty.address_1.trim()
            : null),
        // Remove postcode from address fields if it was found there
        address_1:
          selectedProperty.address_1 &&
          !/^[A-Z]{1,2}\d[A-Z\d]?\s?\d[A-Z]{2}$/i.test(
            selectedProperty.address_1.trim()
          )
            ? selectedProperty.address_1
            : null,
        address_2:
          selectedProperty.address_2 &&
          !/^[A-Z]{1,2}\d[A-Z\d]?\s?\d[A-Z]{2}$/i.test(
            selectedProperty.address_2.trim()
          )
            ? selectedProperty.address_2
            : null,
        address_3:
          selectedProperty.address_3 &&
          !/^[A-Z]{1,2}\d[A-Z\d]?\s?\d[A-Z]{2}$/i.test(
            selectedProperty.address_3.trim()
          )
            ? selectedProperty.address_3
            : null,
      }
    : null;

  // Debug logging for property data
  if (fixedProperty) {
    console.log("Fixed Property Data:", {
      id: fixedProperty.id,
      property_id: fixedProperty.property_id,
      name: fixedProperty.name,
      address_1: fixedProperty.address_1,
      address_2: fixedProperty.address_2,
      address_3: fixedProperty.address_3,
      postcode: fixedProperty.postcode,
    });
  }

  // Debug: Track room_id changes and verify it exists in rooms list
  useEffect(() => {
    const roomIdString = String(form.room_id || "").trim();
    const matchingRoom = propertyRooms.find(r => String(r.id).trim() === roomIdString);
    
    console.log("🔄 Form room_id changed:", {
      room_id: form.room_id,
      type: typeof form.room_id,
      stringValue: roomIdString,
      propertyRooms: propertyRooms.map(r => ({ id: r.id, idType: typeof r.id, name: r.name })),
      matchingRoom: matchingRoom ? { id: matchingRoom.id, name: matchingRoom.name } : null,
      roomExists: !!matchingRoom
    });
    
    if (roomIdString && !matchingRoom) {
      console.warn("⚠️ WARNING: room_id exists in form but not found in propertyRooms list!", {
        formRoomId: roomIdString,
        availableRoomIds: propertyRooms.map(r => String(r.id))
      });
    }
  }, [form.room_id, propertyRooms]);

  // Debug logging for form state
  console.log("Current form state:", form);
  console.log("Selected property:", selectedProperty);
  console.log("Form room_id:", form.room_id, "Type:", typeof form.room_id);
  console.log("Property rooms:", propertyRooms.map(r => ({ id: r.id, type: typeof r.id, name: r.name })));
  console.log("Available properties:", dropdowns.properties?.length || 0);
  console.log("Available types:", dropdowns.ticketTypes?.length || 0);
  console.log("Available urgencies:", dropdowns.urgencies?.length || 0);
  console.log("Available statuses:", dropdowns.statuses?.length || 0);
  console.log(
    "Available complaint statuses:",
    dropdowns.complaintStatuses?.length || 0
  );
  console.log("Complaint statuses data:", dropdowns.complaintStatuses);
  console.log("Form complaint_id:", form.complaint_id);
  console.log("Form complaint_id type:", typeof form.complaint_id);
  console.log("Form is_complaint:", form.is_complaint);
  
  // Enhanced debugging for complaint statuses
  if (form.is_complaint === "1") {
    console.log("🚨 COMPLAINT MODE ACTIVE - Checking complaint statuses:");
    console.log("- complaintStatuses array:", dropdowns.complaintStatuses);
    console.log("- complaintStatuses length:", dropdowns.complaintStatuses?.length);
    console.log("- complaintStatuses type:", typeof dropdowns.complaintStatuses);
    console.log("- Raw response data:", dropdowns);
  }

  // Debug complaint status matching
  if (dropdowns.complaintStatuses && form.complaint_id) {
    const matchingStatus = dropdowns.complaintStatuses.find(
      (cs) => String(cs.id) === String(form.complaint_id)
    );
    console.log("Matching complaint status:", matchingStatus);
    console.log(
      "All complaint status options:",
      dropdowns.complaintStatuses.map((cs) => ({
        id: cs.id,
        id_type: typeof cs.id,
        name: cs.name,
      }))
    );
  }

  // Validation
  const validate = () => {
    const errors = {};

    // Required fields
    if (!form.property_id && !dropdowns.currentUser?.isGuardian) {
      errors.property_id = "Property is required";
    }
    if (!form.type_id) errors.type_id = "Type is required";
    if (!form.urgency_id) errors.urgency_id = "Urgency is required";
    if (!form.description) errors.description = "Description is required";
    if (!form.date_estimated_completion) {
      errors.date_estimated_completion = "Proposed deadline is required";
    }
    if (!form.book_in_date) {
      errors.book_in_date = "Book in date is required";
    }

    // Complaint validation (for all users)
    if (form.is_complaint === "1" && !form.complaint_id) {
      errors.complaint_id = "Complaint status is required";
    }

    // Staff-only validations
    // Exclude: Guardians and Sales with Guardians role
    if (dropdowns.currentUser?.isStaff && !dropdowns.currentUser?.isGuardian && !dropdowns.currentUser?.isSalesWithGuardians) {
      if (!form.status_id && form.is_complaint !== "1") {
        errors.status_id = "Status is required";
      }

      // Assigned Member is required for staff (excluding Guardians and Sales with Guardians)
      if (!form.assigned_member_id) {
        errors.assigned_member_id = "Assigned Member is required";
      }
    }

    // Contractor contact validation
    if (form.contractor_id && !form.contractor_contact_id) {
      errors.contractor_contact_id =
        "Contractor contact is required when a contractor is selected";
    }

    // Proposed deadline validation removed - can be past date

    // Book in date validation removed - can be past date

    // Time validation - same as ticket creation
    if (
      form.book_in_time_from &&
      form.book_in_time_to &&
      form.book_in_time_to <= form.book_in_time_from
    ) {
      errors.book_in_time_to =
        "Book in Time To must be after Book in Time From";
    }

    return errors;
  };

  // Handle property selection from typeahead dropdown
  const handlePropertyChange = (propertyId) => {
    console.log('🏢 Property selected:', propertyId, 'Type:', typeof propertyId);

    const newForm = {
      ...form,
      property_id: propertyId,
      room_id: '' // Reset room when property changes
    };

    setForm(newForm);
    saveFormState(newForm);

    // Handle both string and number comparisons for property ID
    const property = searchResults.find(p =>
      String(p.id) === String(propertyId)
    ) || dropdowns.properties?.find(p =>
      String(p.id) === String(propertyId)
    );

    console.log('🏢 Property data found:', property);

    // Update search text with selected property
    if (property) {
      setPropertySearch(`${property.property_id || property.id} - ${property.name}`);
    }
    setShowPropertyDropdown(false);

    if (propertyId && property) {
      console.log('🏢 Property selected successfully:', property.name);
    } else if (propertyId && !property) {
      console.error('🏢 Property not found for ID:', propertyId);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newForm = {
      ...form,
      [name]: type === "checkbox" ? checked : value,
    };

    // Auto-set date_completed to today when status is changed to "Complete"
    if (name === "status_id") {
      const selectedStatus = dropdowns.statuses?.find(
        (s) => String(s.id) === String(value)
      );
      if (
        selectedStatus &&
        selectedStatus.name.toLowerCase().includes("complete")
      ) {
        if (!newForm.date_completed) {
          newForm.date_completed = new Date().toISOString().split("T")[0];
        }
      }
    }

    setForm(newForm);

    // Save form state to localStorage
    saveFormState(newForm);

    // Real-time validation removed for date fields - both can be past dates

    // Real-time validation for time fields
    if (name === "book_in_time_from" || name === "book_in_time_to") {
      const newForm = { ...form, [name]: value };

      // Clear previous time-related errors
      setFieldErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.book_in_time_from;
        delete newErrors.book_in_time_to;
        return newErrors;
      });

      // Time range validation
      if (
        newForm.book_in_time_from &&
        newForm.book_in_time_to &&
        newForm.book_in_time_to <= newForm.book_in_time_from
      ) {
        setFieldErrors((prev) => ({
          ...prev,
          book_in_time_to: "Book in Time To must be after Book in Time From",
        }));
      }
    }
  };

  const handleDownloadDocument = (document) => {
    // Create download link for document
    const link = document.createElement("a");
    link.href = document.file.startsWith("http")
      ? document.file
      : `http://localhost:3001/${document.file}`;
    link.download = document.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log("Form submission started");
    console.log("Form data:", form);

    const errors = validate();
    console.log("Validation errors:", errors);
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      console.log("Form submission blocked due to validation errors");
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(false);

    // Sanitize form data - convert empty strings to null for numeric/optional fields
    const sanitizedForm = {
      ...form,
      // Convert empty string to null for numeric fields
      cost_of_works: form.cost_of_works === '' ? null : form.cost_of_works,
      // Convert empty string to null for optional fields
      assigned_member_id: form.assigned_member_id === '' ? null : form.assigned_member_id,
      contractor_id: form.contractor_id === '' ? null : form.contractor_id,
      contractor_contact_id: form.contractor_contact_id === '' ? null : form.contractor_contact_id,
      date_completed: form.date_completed === '' ? null : form.date_completed,
      book_in_time_from: form.book_in_time_from === '' ? null : form.book_in_time_from,
      book_in_time_to: form.book_in_time_to === '' ? null : form.book_in_time_to,
      complaint_id: form.complaint_id === '' ? null : form.complaint_id,
      // CRITICAL: room_id is sent to backend but converted to ticketable_id/ticketable_type
      // Backend will handle the conversion to polymorphic relationship
      room_id: form.room_id === '' ? null : (form.room_id ? String(form.room_id) : null),
    };

    // Queue the edit (PUT) for background sync — idempotent, so a retry is safe.
    const queueEditOffline = async () => {
      try {
        await saveDraft({
          id: `tkt-edit-${id}`,
          ticketId: id,
          reportType: 'ticketForm',
          mode: 'update',
          formData: sanitizedForm,
          photos: {},
          status: 'queued',
          attempts: 0,
          permanent: false,
          lastError: null,
        });
      } catch (err) {
        setError(
          isQuotaError(err)
            ? 'This device is out of storage, so the changes could not be saved offline.'
            : 'Offline storage is unavailable, so the changes cannot be saved while offline.'
        );
        return false;
      }
      toast.info('Saved. Your changes will sync automatically when you’re back online.');
      triggerSync();
      clearFormState();
      navigate(`/tickets/${id}`);
      return true;
    };

    const isOnline = typeof navigator === 'undefined' ? true : navigator.onLine;
    if (!isOnline) {
      const queued = await queueEditOffline();
      if (!queued) setSaving(false);
      return;
    }

    try {

      console.log("🔍 [FRONTEND] Form submission - room_id being sent:", {
        form_room_id: form.room_id,
        form_room_id_type: typeof form.room_id,
        sanitized_room_id: sanitizedForm.room_id,
        sanitized_room_id_type: typeof sanitizedForm.room_id,
        property_id: sanitizedForm.property_id,
        note: "Backend will convert room_id to ticketable_id + ticketable_type"
      });
      console.log("Calling ticketsService.updateTicket with:", { id, sanitizedForm });
      await ticketsService.updateTicket(id, sanitizedForm);
      setSuccess(true);
      // Clear saved form state on successful submission
      clearFormState();
      setTimeout(() => navigate(`/tickets/${id}`), 1000);
    } catch (err) {
      console.error("Error updating ticket:", err);
      // Network failure (no server response) → queue the edit for sync.
      if (!err?.response) {
        const queued = await queueEditOffline();
        if (queued) return;
      }
      setError(err.message || "Failed to update ticket");
      setSaving(false);
    }
  };



  const handleDeleteNote = async (noteId) => {
    if (!window.confirm("Are you sure you want to delete this note?")) return;
    setDeleteLoadingId(noteId);
    setDeleteError(null);
    try {
      const response = await ticketsService.deleteTicketNote(noteId);

      // Delete associated files from S3 if any documents were deleted
      if (response.deletedDocuments && response.deletedDocuments.length > 0) {
        console.log(
          `Deleting ${response.deletedDocuments.length} files from S3 for note ${noteId}`
        );

        for (const document of response.deletedDocuments) {
          try {
            await s3Service.deleteFile(document.file);
            console.log(`Successfully deleted S3 file: ${document.name}`);
          } catch (s3Error) {
            console.error(
              `Failed to delete S3 file ${document.name}:`,
              s3Error
            );
            // Continue with other files even if one fails
          }
        }
      }

      const notesData = await ticketsService.getTicketNotes(id);
      setNotes(notesData);
      setError(null); // Clear any previous errors
    } catch (err) {
      console.error("Error deleting note:", err);
      setDeleteError(err.message || "Failed to delete note");
    } finally {
      setDeleteLoadingId(null);
    }
  };

  // 🔥 DEBUG: Version marker to verify correct code is loading
  console.log("🔥 TICKET EDIT COMPONENT VERSION: 2024-10-18-FINAL-PROPERTYROOMS-FIX");
  console.log("🔥 Using ticket.propertyRooms:", ticket?.propertyRooms?.length || 0, "rooms");

  if (loading) return <Loader />;
  if (error)
    return <div style={{ color: "red", padding: "20px" }}>Error: {error}</div>;
  if (!ticket) return <div style={{ padding: "20px" }}>Ticket not found</div>;

  return (
    <div className="container" style={{ maxWidth: 1200, margin: "0 auto" }}>
      <div className="breadcrumb-row">
        <span
          className="breadcrumb-main"
          style={{ fontWeight: 400, fontSize: "14px", color: "black", cursor: 'pointer' }}
          onClick={() => navigate('/dashboard')}
        >
          Dashboard
        </span>
        <span
          className="breadcrumb-main"
          style={{ fontWeight: 400, fontSize: "14px", color: "black", cursor: 'pointer' }}
          onClick={() => navigate('/tickets')}
        >
          / Tickets
        </span>
        <div
          style={{ background: "#30C693", fontWeight: 400, fontSize: "14px" }}
        >
          <span className="breadcrumb-sep">/</span>
          <span className="breadcrumb-properties">#{ticket.id}</span>
        </div>
      </div>{" "}
      {/* General Error Display */}
      {error && (
        <div
          style={{
            color: "#d9534f",
            background: "#f8d7da",
            border: "1px solid #f5c6cb",
            borderRadius: 4,
            padding: 12,
            marginBottom: 16,
            fontSize: 14,
          }}
        >
          {error}
        </div>
      )}
      <form onSubmit={handleSubmit}>
        {/* Two-column layout for property and main form */}
        <div
          className="edit-choose-property-overview-wrapper"
          // style={{ display: "flex", gap: 32 }}
        >
          {/* Left: Property selection/search */}
          <div className="edit-choose-property">
            <div className="panel mb-4">
              <div className="edit-choose-property-title">
                <h3>Choose property</h3>
              </div>
              <div className="panel-body">
                <label
                  htmlFor="property-search"
                  className="edit-choose-property-label "
                >
                  Search for property
                </label>
                <div style={{ position: 'relative' }} ref={propertyDropdownRef}>
                  <div style={{ position: 'relative' }}>
                    <input
                      id="property-search"
                      type="text"
                      className="form-control"
                      placeholder="Type to search properties..."
                      value={propertySearch}
                      onChange={(e) => {
                        setPropertySearch(e.target.value);
                        setShowPropertyDropdown(true);
                        searchPropertiesApi(e.target.value);
                      }}
                      onFocus={() => setShowPropertyDropdown(true)}
                      style={{
                        marginBottom: 0,
                        paddingRight: 32
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPropertyDropdown(!showPropertyDropdown)}
                      style={{
                        position: 'absolute',
                        right: 8,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: 4,
                        color: '#666'
                      }}
                    >
                      ▼
                    </button>
                  </div>
                  {showPropertyDropdown && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      maxHeight: 300,
                      overflowY: 'auto',
                      background: '#fff',
                      border: '1px solid #ccc',
                      borderRadius: '0 0 4px 4px',
                      boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                      zIndex: 9999
                    }}>
                      {searchingProperties ? (
                        <div style={{ padding: '8px 12px', color: '#999' }}>
                          Searching...
                        </div>
                      ) : propertySearch.trim().length < 2 ? (
                        <div style={{ padding: '8px 12px', color: '#999' }}>
                          Type at least 2 characters to search
                        </div>
                      ) : filteredProperties.length > 0 ? (
                        filteredProperties.map(property => (
                          <div
                            key={property.id}
                            onClick={() => handlePropertyChange(property.id)}
                            style={{
                              padding: '8px 12px',
                              cursor: 'pointer',
                              borderBottom: '1px solid #eee',
                              background: String(form.property_id) === String(property.id) ? '#e8f4fd' : '#fff',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              fontSize: '14px'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = '#f0f0f0'}
                            onMouseLeave={(e) => e.currentTarget.style.background = String(form.property_id) === String(property.id) ? '#e8f4fd' : '#fff'}
                          >
                            {property.property_id || property.id} - {property.name}
                          </div>
                        ))
                      ) : (
                        <div style={{ padding: '8px 12px', color: '#999' }}>
                          No properties found
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {fieldErrors.property_id && (
                  <div
                    style={{
                      color: "#d9534f",
                      fontSize: "12px",
                      marginTop: "4px",
                    }}
                  >
                    {fieldErrors.property_id}
                  </div>
                )}
              </div>
            </div>
          </div>
          {/* Right: Property overview */}
          <div style={{ flex: 1.5, minWidth: 320 }}>
            <div className="panel mb-4">
              <div className="edit-choose-property-title">
                <h3>Property overview</h3>
              </div>
              <div className="panel-body">
                {fixedProperty ? (
                  <>
                    <div className="ticket_details_items">
                      <b>ID:</b>
                      <span>
                        {fixedProperty.property_id || fixedProperty.id}
                      </span>
                    </div>
                    <div className="ticket_details_items">
                      <b>Property name:</b> <span>{fixedProperty.name}</span>
                    </div>
                    <div className="ticket_details_items">
                      <b>Property address:</b>{" "}
                      <span>
                        {fixedProperty.address_1 && (
                          <div>{fixedProperty.address_1}</div>
                        )}
                        {fixedProperty.address_2 && (
                          <div>{fixedProperty.address_2}</div>
                        )}
                        {fixedProperty.address_3 && (
                          <div>{fixedProperty.address_3}</div>
                        )}
                        {!fixedProperty.address_1 &&
                          !fixedProperty.address_2 &&
                          !fixedProperty.address_3 && (
                            <div>{fixedProperty.name}</div>
                          )}
                      </span>
                    </div>
                    <div className="property-overview">
                      <div className="ticket_details_items">
                        <b>Postcode:</b>
                        <span style={{ margin: 0 }}>
                          {fixedProperty.postcode || "-"}
                        </span>
                      </div>
                    </div>

                    <div>
                      <dt>Select rooms</dt>
                    </div>
                    {propertyRooms.length === 0 ? (
                      <div style={{ color: "#888" }}>
                        No rooms found for this property.
                      </div>
                    ) : (
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: "12px",
                        }}
                      >
                        {propertyRooms.map((room) => (
                          <div
                            key={room.id}
                            className="room-information"
                            style={{
                              width: "calc(33.333% - 8px)",
                              minWidth: "200px",
                            }}
                          >
                            {/* Room number header */}
                            <p className="room-number">Room {room.name}</p>

                            {/* Room information section */}
                            <ul className="room-information-in">
                              {/* Number of keys row */}
                              <li>
                                <span>Number of keys:</span>{" "}
                                {room.key_number || 0}
                              </li>

                              {/* Laravel-compatible room selection UI */}
                              {(() => {
                                // Normalize both values to strings for comparison
                                const currentRoomId = String(form.room_id || "").trim();
                                const roomId = String(room.id || "").trim();
                                const isSelected = currentRoomId !== "" && currentRoomId === roomId;
                                
                                // Debug log for each room render
                                if (propertyRooms.length <= 3) { // Only log for small lists to avoid spam
                                  console.log(`🎨 Room "${room.name}" render:`, {
                                    roomId: roomId,
                                    formRoomId: currentRoomId,
                                    isSelected: isSelected,
                                    comparison: `"${currentRoomId}" === "${roomId}"`
                                  });
                                }
                                
                                return (
                                  <li
                                    key={`room-${roomId}`}
                                    className={`select-room ${isSelected ? "selected" : ""}`}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      const newRoomId = isSelected ? "" : roomId;
                                      console.log("🔍 Room selection clicked:", {
                                        roomName: room.name,
                                        currentRoomId: currentRoomId,
                                        clickedRoomId: roomId,
                                        newRoomId: newRoomId,
                                        wasSelected: isSelected
                                      });
                                      
                                      // Update form state
                                      const updatedForm = {
                                        ...form,
                                        room_id: newRoomId,
                                      };
                                      
                                      console.log("✅ Form state before update:", form.room_id);
                                      console.log("✅ Form state after update:", updatedForm.room_id);
                                      
                                      setForm(updatedForm);
                                      
                                      // Save form state to localStorage
                                      saveFormState(updatedForm);
                                    }}
                                    style={{ 
                                      cursor: "pointer",
                                      backgroundColor: isSelected ? "rgb(156, 255, 164)" : "#e0e0e0",
                                      color: isSelected ? "#000" : "#0a7280",
                                      fontWeight: isSelected ? "bold" : "normal",
                                      transition: "background-color 0.2s ease",
                                      // Force override any CSS with !important via inline style
                                      border: isSelected ? "2px solid #28a745" : "1px solid #ccc"
                                    }}
                                  >
                                    {isSelected ? (
                                      <>
                                        <span style={{ marginRight: "5px", fontWeight: "bold", fontSize: "16px", color: "#000" }}>
                                          ✓
                                        </span>
                                        <span style={{ color: "#000" }}>Selected room</span>
                                      </>
                                    ) : (
                                      <>
                                        <span style={{ marginRight: "5px", fontSize: "14px" }}>
                                          ›
                                        </span>
                                        Select this room
                                      </>
                                    )}
                                  </li>
                                );
                              })()}
                            </ul>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <div style={{ color: "#888" }}>No property selected.</div>
                )}
              </div>
            </div>
          </div>
          {/* Right: Main form sections */}
        </div>
        <div style={{ flex: 2, minWidth: 0 }}>
          {/* Issue panel */}
          <div className="panel mb-4 mt-4">
            <div className="edit-choose-property-title">
              <h3>Issue</h3>
            </div>
            <div className="panel-body">
              {/* Move all issue-related fields here (type, description, internal note, etc.) */}
              <div className="form-group create-property-input-field-Status">
                <label className="label-required">Type</label>
                <select
                  className="form-control"
                  name="type_id"
                  value={form.type_id}
                  onChange={handleChange}
                  required
                  style={{
                    border: fieldErrors.type_id
                      ? "1px solid #d9534f"
                      : "1px solid #ccc",
                  }}
                >
                  <option value="">Select type</option>
                  {dropdowns.ticketTypes && dropdowns.ticketTypes.length > 0 ? (
                    dropdowns.ticketTypes.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))
                  ) : (
                    <option value="" disabled>
                      Loading types...
                    </option>
                  )}
                </select>
                {fieldErrors.type_id && (
                  <div
                    style={{
                      color: "#d9534f",
                      fontSize: "12px",
                      marginTop: "4px",
                    }}
                  >
                    {fieldErrors.type_id}
                  </div>
                )}
              </div>
              <div className="form-group create-property-input-field-Status">
                <label className="label-required">Urgency</label>
                <select
                  className="form-control"
                  name="urgency_id"
                  value={form.urgency_id}
                  onChange={handleChange}
                  required
                  style={{
                    border: fieldErrors.urgency_id
                      ? "1px solid #d9534f"
                      : "1px solid #ccc",
                  }}
                >
                  <option value="">Select urgency</option>
                  {dropdowns.urgencies && dropdowns.urgencies.length > 0 ? (
                    dropdowns.urgencies.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.name}
                      </option>
                    ))
                  ) : (
                    <option value="" disabled>
                      Loading urgencies...
                    </option>
                  )}
                </select>
                {fieldErrors.urgency_id && (
                  <div
                    style={{
                      color: "#d9534f",
                      fontSize: "12px",
                      marginTop: "4px",
                    }}
                  >
                    {fieldErrors.urgency_id}
                  </div>
                )}
              </div>

              <div className="form-group create-property-textarea-field">
                <label className="label-required">Description</label>
                <textarea
                  className="form-control"
                  name="description"
                  value={form.description || ""}
                  onChange={handleChange}
                  rows={4}
                  required
                  style={{
                    border: fieldErrors.description
                      ? "1px solid #d9534f"
                      : "1px solid #ccc",
                  }}
                />
                {fieldErrors.description && (
                  <div
                    style={{
                      color: "#d9534f",
                      fontSize: "12px",
                      marginTop: "4px",
                    }}
                  >
                    {fieldErrors.description}
                  </div>
                )}
              </div>
              <div className="form-group create-property-textarea-field">
                <label>Internal Note</label>
                <textarea
                  className="form-control"
                  name="internal_note"
                  value={form.internal_note}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group create-property-input-field">
                <label className="label-required">Proposed Deadline</label>
                <input
                  className="form-control"
                  name="date_estimated_completion"
                  value={form.date_estimated_completion || ""}
                  onChange={handleChange}
                  type="date"
                  required
                  style={{
                    border: fieldErrors.date_estimated_completion
                      ? "1px solid #d9534f"
                      : "1px solid #ccc",
                  }}
                />
                {fieldErrors.date_estimated_completion && (
                  <div
                    style={{
                      color: "#d9534f",
                      fontSize: "12px",
                      marginTop: "4px",
                    }}
                  >
                    {fieldErrors.date_estimated_completion}
                  </div>
                )}
              </div>
              <div className="form-group create-property-input-field">
                <label>Date Completed</label>
                <input
                  className="form-control"
                  name="date_completed"
                  value={form.date_completed || ""}
                  onChange={handleChange}
                  type="date"
                />
              </div>
              <div className="form-group create-property-input-field">
                <label className="label-required">Book In Date</label>
                <input
                  className="form-control"
                  name="book_in_date"
                  value={form.book_in_date || ""}
                  onChange={handleChange}
                  type="date"
                  required
                  style={{
                    border: fieldErrors.book_in_date
                      ? "1px solid #d9534f"
                      : "1px solid #ccc",
                  }}
                />
                {fieldErrors.book_in_date && (
                  <div
                    style={{
                      color: "#d9534f",
                      fontSize: "12px",
                      marginTop: "4px",
                    }}
                  >
                    {fieldErrors.book_in_date}
                  </div>
                )}
              </div>
              <div className="form-group create-property-input-field">
                <label>Book In Time From</label>
                <input
                  className="form-control"
                  name="book_in_time_from"
                  value={form.book_in_time_from || ""}
                  onChange={handleChange}
                  type="time"
                />
                {fieldErrors.book_in_time_from && (
                  <div
                    style={{
                      color: "#d9534f",
                      fontSize: "12px",
                      marginTop: "4px",
                    }}
                  >
                    {fieldErrors.book_in_time_from}
                  </div>
                )}
              </div>
              <div className="form-group create-property-input-field">
                <label>Book In Time To</label>
                <input
                  className="form-control"
                  name="book_in_time_to"
                  value={form.book_in_time_to || ""}
                  onChange={handleChange}
                  type="time"
                />
                {fieldErrors.book_in_time_to && (
                  <div
                    style={{
                      color: "#d9534f",
                      fontSize: "12px",
                      marginTop: "4px",
                    }}
                  >
                    {fieldErrors.book_in_time_to}
                  </div>
                )}
              </div>
              <div className="form-group create-property-input-field">
                <label>Cost of Works</label>
                <input
                  className="form-control"
                  name="cost_of_works"
                  value={form.cost_of_works || ""}
                  onChange={handleChange}
                  type="number"
                  step="0.01"
                />
              </div>
              <div className="form-group create-property-input-field-Status">
                <label className={!dropdowns.currentUser?.isGuardian && !dropdowns.currentUser?.isSalesWithGuardians ? "label-required" : ""}>
                  Assigned Member
                  {!dropdowns.currentUser?.isGuardian && !dropdowns.currentUser?.isSalesWithGuardians && <span style={{ color: '#d9534f' }}> *</span>}
                </label>
                <select
                  className="form-control"
                  name="assigned_member_id"
                  value={form.assigned_member_id}
                  onChange={handleChange}
                  required={!dropdowns.currentUser?.isGuardian && !dropdowns.currentUser?.isSalesWithGuardians}
                  style={{
                    border: fieldErrors.assigned_member_id
                      ? "1px solid #d9534f"
                      : "1px solid #ccc",
                  }}
                >
                  <option value="">Select user</option>
                  {Array.isArray(dropdowns.users) && dropdowns.users.length > 0 ? (
                    dropdowns.users.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.name}
                      </option>
                    ))
                  ) : (
                    <option value="" disabled>
                      Loading users...
                    </option>
                  )}
                </select>
                {fieldErrors.assigned_member_id && (
                  <div
                    style={{
                      color: "#d9534f",
                      fontSize: "12px",
                      marginTop: "4px",
                    }}
                  >
                    {fieldErrors.assigned_member_id}
                  </div>
                )}
              </div>

              {/* Certificate renewal (staff only, certification ticket types only) */}
              {dropdowns.currentUser?.isStaff &&
                !!certificateTypeFromServiceName(
                  dropdowns.ticketTypes?.find(
                    (t) => String(t.id) === String(form.type_id)
                  )?.name
                ) && (
                <div
                  className="form-group create-property-input-field-Status"
                  title="Only renewal tickets show the certificate upload panel and go through the review queue."
                >
                  <label>Certificate Renewal</label>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "140px",
                      marginLeft: "16px",
                    }}
                  >
                    <input
                      type="checkbox"
                      name="is_certificate_renewal"
                      checked={form.is_certificate_renewal === "1"}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          is_certificate_renewal: e.target.checked ? "1" : "0",
                        }))
                      }
                      style={{ transform: "scale(1.1)" }}
                    />
                  </div>
                </div>
              )}

              {/* Client View (staff only) */}
              {dropdowns.currentUser?.isStaff && (
                <div className="form-group create-property-input-field-Status">
                  <label>Client View</label>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "140px",
                      marginLeft: "16px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "40px",
                      }}
                    >
                      <input
                        type="radio"
                        name="client_view"
                        value="0"
                        checked={form.client_view === "0"}
                        onChange={handleChange}
                      />
                      <label style={{ fontWeight: 500, color: "#333333" }}>
                        No
                      </label>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "40px",
                        marginLeft: "32px",
                      }}
                    >
                      <input
                        type="radio"
                        name="client_view"
                        value="1"
                        checked={form.client_view === "1"}
                        onChange={handleChange}
                      />{" "}
                      <label style={{ fontWeight: 500, color: "#333333" }}>
                        Yes
                      </label>
                    </div>
                  </div>
                </div>
              )}

              {/* Guardian View (staff only) */}
              {dropdowns.currentUser?.isStaff && (
                <div className="form-group create-property-input-field-Status">
                  <label>Guardian View</label>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "140px",
                      marginLeft: "16px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "40px",
                      }}
                    >
                      <input
                        type="radio"
                        name="guardian_view"
                        value="0"
                        checked={form.guardian_view === "0"}
                        onChange={handleChange}
                      />
                      <label style={{ fontWeight: 500, color: "#333333" }}>
                        No
                      </label>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "40px",
                        marginLeft: "32px",
                      }}
                    >
                      <input
                        type="radio"
                        name="guardian_view"
                        value="1"
                        checked={form.guardian_view === "1"}
                        onChange={handleChange}
                      />{" "}
                      <label style={{ fontWeight: 500, color: "#333333" }}>
                        Yes
                      </label>
                    </div>
                  </div>
                </div>
              )}

              <div className="form-group create-property-input-field-Status">
                <label>Is Complaint?</label>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "140px",
                    marginLeft: "16px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "40px",
                    }}
                  >
                    <input
                      type="radio"
                      name="is_complaint"
                      value="0"
                      checked={form.is_complaint === "0"}
                      onChange={handleChange}
                    />
                    <label style={{ fontWeight: 500, color: "#333333" }}>
                      No
                    </label>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "40px",
                      marginLeft: "32px",
                    }}
                  >
                    <input
                      type="radio"
                      name="is_complaint"
                      value="1"
                      checked={form.is_complaint === "1"}
                      onChange={handleChange}
                    />{" "}
                    <label style={{ fontWeight: 500, color: "#333333" }}>
                      Yes
                    </label>
                  </div>
                </div>
              </div>

              {/* Dynamic Status Field - shows either Ticket Status or Complaint Status based on is_complaint */}
              {form.is_complaint === "1" ? (
                <div className="form-group create-property-input-field-Status">
                  <label className="label-required">Complaint Status</label>
                  <select
                    className="form-control"
                    name="complaint_id"
                    value={form.complaint_id}
                    onChange={handleChange}
                    style={{
                      border: fieldErrors.complaint_id
                        ? "1px solid #d9534f"
                        : "1px solid #ccc",
                    }}
                  >
                    <option value="">Select complaint status</option>
                    {dropdowns.complaintStatuses &&
                    dropdowns.complaintStatuses.length > 0 ? (
                      dropdowns.complaintStatuses.map((cs) => (
                        <option key={cs.id} value={cs.id}>
                          {cs.name}
                        </option>
                      ))
                    ) : (
                      <option value="" disabled>
                        Loading complaint statuses...
                      </option>
                    )}
                  </select>
                  {fieldErrors.complaint_id && (
                    <div
                      style={{
                        color: "#d9534f",
                        fontSize: "12px",
                        marginTop: "4px",
                      }}
                    >
                      {fieldErrors.complaint_id}
                    </div>
                  )}
                </div>
              ) : (
                <div className="form-group create-property-input-field-Status">
                  <label className="label-required">Ticket Status</label>
                  <select
                    className="form-control"
                    name="status_id"
                    value={form.status_id}
                    onChange={handleChange}
                    style={{
                      border: fieldErrors.status_id
                        ? "1px solid #d9534f"
                        : "1px solid #ccc",
                    }}
                  >
                    <option value="">Select ticket status</option>
                    {dropdowns.statuses && dropdowns.statuses.length > 0 ? (
                      dropdowns.statuses.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                        </option>
                      ))
                    ) : (
                      <option value="" disabled>
                        Loading statuses...
                      </option>
                    )}
                  </select>
                  {fieldErrors.status_id && (
                    <div
                      style={{
                        color: "#d9534f",
                        fontSize: "12px",
                        marginTop: "4px",
                      }}
                    >
                      {fieldErrors.status_id}
                    </div>
                  )}
                </div>
              )}
              {/* Guardian-specific: Issue With */}
              {dropdowns.currentUser?.isGuardian && (
                <div className="form-group">
                  <label>Issue With</label>
                  <select
                    className="form-control"
                    name="issue_with"
                    value={form.issue_with}
                    onChange={handleChange}
                  >
                    <option value="">Select</option>
                    <option value="room">My room</option>
                    <option value="property">My property</option>
                  </select>
                </div>
              )}
            </div>
          </div>
          {/* Contractor panel */}
          <div className="panel mb-4">
            <div className="edit-choose-property-title">
              <h3 className="edit-choose-property-title">Contractor</h3>
            </div>
            <div className="panel-body">
              {/* Contractor-related fields */}
              <div className="form-group create-property-input-field-Status">
                <label>Contractor</label>
                <select
                  className="form-control"
                  name="contractor_id"
                  value={form.contractor_id}
                  onChange={handleChange}
                >
                  <option value="">Select contractor</option>
                  {dropdowns.contractors && dropdowns.contractors.length > 0 ? (
                    dropdowns.contractors.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))
                  ) : (
                    <option value="" disabled>
                      Loading contractors...
                    </option>
                  )}
                </select>
              </div>
              <div className="form-group create-property-input-field-Status">
                <label className="label-required">Contractor Contact</label>
                <select
                  className="form-control"
                  name="contractor_contact_id"
                  value={form.contractor_contact_id}
                  onChange={handleChange}
                  style={{
                    border: fieldErrors.contractor_contact_id
                      ? "1px solid #d9534f"
                      : "1px solid #ccc",
                  }}
                >
                  <option value="">Select contact</option>
                  {contractorContacts && contractorContacts.length > 0 ? (
                    contractorContacts.map((cc) => (
                      <option key={cc.id} value={cc.id}>
                        {cc.name}
                      </option>
                    ))
                  ) : (
                    <option value="" disabled>
                      {form.contractor_id
                        ? "No contacts found for this contractor"
                        : "Select a contractor first"}
                    </option>
                  )}
                </select>
                {fieldErrors.contractor_contact_id && (
                  <div
                    style={{
                      color: "#d9534f",
                      fontSize: "12px",
                      marginTop: "4px",
                    }}
                  >
                    {fieldErrors.contractor_contact_id}
                  </div>
                )}
              </div>
              <div className="form-group create-property-textarea-field">
                <label>Equipment Purchased</label>
                <textarea
                  className="form-control"
                  name="equipment_purchased"
                  value={form.equipment_purchased}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>
          {/* Ticket Notes panel */}
          <div className="panel mb-4">
            <div
              className="panel-title"
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <h3>Ticket notes</h3>
              <button
                className="btn-action"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  navigate(`/tickets/${id}/notes/add`);
                }}
                style={{
                  backgroundColor: "#1B2046",
                  color: "#fff",
                  border: "none",
                  padding: "6px 12px",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "12px",
                }}
              >
                <i className="glyphicon glyphicon-plus"></i> Add Notes
              </button>
            </div>
            <div className="panel-body">
              <table className="table table-hover table-condensed">
                <thead>
                  <tr>
                    <th>Description</th>
                    <th>Note updated at</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {notes.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="text-center text-muted">
                        No notes yet.
                      </td>
                    </tr>
                  ) : (
                    notes.map((note) => (
                      <tr key={note.id}>
                        <td>
                          <a
                            href={`/tickets/${id}/notes/${note.id}`}
                            onClick={(e) => {
                              e.preventDefault();
                              navigate(`/tickets/${id}/notes/${note.id}`);
                            }}
                            style={{
                              padding: "0px 3px",
                              fontSize: "10px",
                              marginRight: "5px",
                              cursor: "pointer"
                            }}
                          >
                            Note #{note.id}
                          </a>
                        </td>
                        <td>
                          {note.updated_at && (
                            <span className="small" style={{ width: "100%" }}>
                              {new Date(note.updated_at).toLocaleString('en-GB', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                                second: '2-digit'
                              })}
                              {note.user && ` by ${note.user.first_name} ${note.user.last_name}`}
                            </span>
                          )}
                        </td>
                        <td>
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              navigate(`/tickets/${id}/notes/${note.id}`);
                            }}
                            className="btn btn-sm btn-warning"
                            style={{
                              padding: "0px 3px",
                              fontSize: "10px",
                              marginRight: "5px"
                            }}
                          >
                            Show
                          </button>
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              navigate(`/tickets/${id}/notes/${note.id}/edit`);
                            }}
                            className="btn btn-sm btn-info"
                            style={{
                              padding: "0px 3px",
                              fontSize: "10px",
                              marginRight: "5px"
                            }}
                          >
                            Edit
                          </button>
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              if (window.confirm("Are you sure you want to remove?")) {
                                handleDeleteNote(note.id);
                              }
                            }}
                            className="btn btn-sm btn-danger"
                            disabled={deleteLoadingId === note.id}
                            style={{
                              padding: "0px 3px",
                              fontSize: "10px",
                              marginRight: "2px"
                            }}
                          >
                            Delete
                          </button>
                          {deleteLoadingId === note.id && deleteError && (
                            <div style={{ color: "red", marginTop: 4, fontSize: "11px" }}>
                              {deleteError}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
          {/* Confirmation panel */}
          <div className="panel" style={{ height: "179px" }}>
            <div className="">
              <h3 className="edit-choose-property-title">Confirmation</h3>
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <button
                className="btn-action"
                style={{ width: "183px", height: "32px" }}
                type="submit"
                disabled={saving}
              >
                {saving ? "Saving..." : "Save Changes"}
              </button>
              {success && (
                <span style={{ color: "green", marginLeft: 12 }}>Saved!</span>
              )}
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default TicketEdit;
